import React, { useState, useCallback, useEffect, useRef } from 'react';
import { apiService } from '../../services/apiService';
import { FileUploader } from './FileUploader';
import { UploadFile, ExtractedReport, ExtractedLabResult, UploadSession as UploadSessionType } from '../../types/upload';
import { API_BASE_URL } from '../../App';
import { useSimpleAuth } from '../../contexts/SimpleAuthContext';
import { UnitCorrectionService } from '../../services/unitCorrectionService';
import ProcessingOverlay from '../ProcessingOverlay';
import DataTable from '../DataTable';
import { LabValue } from '../../types';
import { SuccessToast } from '../SuccessToast';
import { ExtractionValidationService } from '../../services/extractionValidationService';
import { convertReferenceRange, detectReferenceRangeUnit } from '../../utils/referenceRangeConverter';
import LLMExplanation from '../LLMExplanation';

interface UploadSessionProps {
  onComplete: (reports: ExtractedReport[]) => void | Promise<void>;
  onCancel: () => void;
  onNavigateToTrends?: () => void;
}

export const UploadSession: React.FC<UploadSessionProps> = ({ onComplete, onCancel, onNavigateToTrends }) => {
  const { user, isAuthenticated } = useSimpleAuth();
  const sessionId = user?.id.toString() || 'anonymous';
  const [session, setSession] = useState<UploadSessionType>({
    files: [],
    allExtracted: false,
    hasErrors: false,
    confirmationStatus: 'pending'
  });

  const [currentStep, setCurrentStep] = useState<'upload' | 'processing' | 'complete'>('upload');
  const [isSaving, setIsSaving] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isShowingProcessingOverlay, setIsShowingProcessingOverlay] = useState(false);
  const [totalFilesToProcess, setTotalFilesToProcess] = useState(0);
  const [filesProcessed, setFilesProcessed] = useState(0);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number>(0);
  const [showDataTable, setShowDataTable] = useState(false);
  const [skipBackendCalls, setSkipBackendCalls] = useState(false);
  const skipBackendCallsRef = useRef(false);
  const [hasCompletedSave, setHasCompletedSave] = useState(false);
  const [corrections, setCorrections] = useState<Map<string, { value: number; unit?: string; fileId?: string }>>(new Map());
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [hasReviewedData, setHasReviewedData] = useState(false);
  // Column mapping removed - LLM handles everything now
  
  // State for LLM explanation modal
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [llmExplanation, setLlmExplanation] = useState<string | null>(null);
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);
  const [explanationError, setExplanationError] = useState<string | null>(null);
  
  // Add state for user-provided test date
  const [userProvidedTestDate, setUserProvidedTestDate] = useState<string>(
    new Date().toISOString().split('T')[0] // Default to today
  );
  
  // Track file hashes within this batch to detect duplicates
  const batchFileHashesRef = useRef<Map<string, { id: string; filename: string }>>(new Map());
  
  // Handle marker click for LLM explanation
  const handleMarkerClick = async (marker: string, value: number, units: string) => {
    console.log('Marker clicked for explanation:', marker, value, units);
    
    setSelectedMarker(marker);
    setLlmExplanation(null);
    setIsLoadingExplanation(true);
    setExplanationError(null);
    
    try {
      const response = await apiService.post('/api/llm_explanation', {
        marker,
        value,
        units
      });
      
      if (response.status === 'success' && response.data?.explanation) {
        setLlmExplanation(response.data.explanation);
      } else {
        setExplanationError('Unable to get explanation for this lab value.');
      }
    } catch (error: any) {
      console.error('LLM explanation error:', error);
      
      let errorMessage = 'Failed to get explanation for this lab value.';
      if (error.response?.status === 400) {
        errorMessage = 'This lab value is not available for explanation.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setExplanationError(errorMessage);
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  // Apply unit corrections silently for corrections with > 50% confidence
  const applyCorrectionsSilently = useCallback(async (
    results: ExtractedLabResult[], 
    testDate: string | null
  ): Promise<ExtractedLabResult[]> => {
    const correctedResults = [...results];
    
    for (let i = 0; i < correctedResults.length; i++) {
      const result = correctedResults[i];
      if (result.value && result.units) {
        try {
          const correction = await UnitCorrectionService.analyzeAndCorrectUnit(
            result.testName,
            result.value,
            result.units,
            {
              min: result.referenceMin,
              max: result.referenceMax
            },
            sessionId
          );
          
          // Auto-apply corrections with high confidence (>50%)
          if (correction.correctionMethod !== 'none' && 
              (correction.confidence === 'high' || correction.confidence === 'medium')) {
            correctedResults[i] = {
              ...result,
              value: correction.correctedValue,
              units: correction.correctedUnit,
              isEdited: true // Mark as edited so it's clear it was auto-corrected
            };
            console.log(`Auto-corrected ${result.testName}: ${result.value} ${result.units} → ${correction.correctedValue} ${correction.correctedUnit}`);
          }
        } catch (error) {
          console.error(`Failed to analyze units for ${result.testName}:`, error);
        }
      }
    }
    
    return correctedResults;
  }, [isAuthenticated]);

  // Detect file type based on extension
  const getFileType = (filename: string): 'pdf' | 'csv' | 'excel' => {
    const extension = filename.toLowerCase().split('.').pop();
    if (extension === 'pdf') return 'pdf';
    if (extension === 'csv') return 'csv';
    if (extension === 'xls' || extension === 'xlsx') return 'excel';
    return 'pdf'; // Default to PDF
  };


  // Extract data from a single file
  const extractDataFromFile = useCallback(async (uploadFile: UploadFile, fileIndex: number, totalFiles: number) => {
    // Calculate overall progress based on file index
    const baseProgress = (fileIndex / totalFiles) * 100;
    const stageProgress = (1 / totalFiles) * 100;

    setSession(prev => ({
      ...prev,
      files: prev.files.map(f => 
        f.id === uploadFile.id ? { ...f, status: 'processing' } : f
      )
    }));

    const formData = new FormData();
    formData.append('file', uploadFile.file);
    formData.append('userProvidedTestDate', userProvidedTestDate);
    
    // Check if user is authenticated
    if (!user || !isAuthenticated) {
      console.error('No authentication available!');
      throw new Error('Authentication required');
    }

    // Detect file type
    const fileType = getFileType(uploadFile.file.name);
    const isCSVOrExcel = fileType === 'csv' || fileType === 'excel';

    try {
      // Update progress for current file stage 1: Upload
      setProcessingStage(0);
      setProcessingProgress(baseProgress + (stageProgress * 0.2));
      
      if (!isCSVOrExcel) {
        // Stage 2: Read PDF content
        setTimeout(() => {
          setProcessingStage(1);
          setProcessingProgress(baseProgress + (stageProgress * 0.25));
        }, 300);
        
        // Stage 3: Redact personal information
        setTimeout(() => {
          setProcessingStage(2);
          setProcessingProgress(baseProgress + (stageProgress * 0.35));
        }, 600);
        
        // Stage 4: Extract lab values (set before API call)
        setTimeout(() => {
          setProcessingStage(3);
          setProcessingProgress(baseProgress + (stageProgress * 0.5));
        }, 1200);
      }
      
      // Choose appropriate endpoint based on file type
      let endpoint = `${API_BASE_URL}/api/extract-batch`;
      if (isCSVOrExcel) {
        // For CSV/Excel, we'll need to handle column mapping
        // For now, just preview the file
        endpoint = `${API_BASE_URL}/api/import/csv/preview`;
      }
      
      // Use apiService to handle authentication via cookies
      const response = isCSVOrExcel ? 
        await apiService.previewCSV(formData) :
        await apiService.extractBatch(formData);
      
      if (response.status === 'success') {
        const extractedData = response.data;
        
        // Handle CSV/Excel files differently
        if (isCSVOrExcel) {
          // Check if we should skip mapping (LLM parser handles everything)
          if (extractedData.skip_mapping) {
            // Process directly with LLM parser
            console.log('Processing file with AI parser...');
            
            // Store data in refs to avoid state timing issues
            const processData = {
              tempFilePath: extractedData.tempFilePath || extractedData.temp_file_path
            };
            
            // Update file status to processing
            setSession(prev => ({
              ...prev,
              files: prev.files.map(f => 
                f.id === uploadFile.id ? { ...f, status: 'processing' } : f
              )
            }));
            
            // Process the CSV import directly
            try {
              console.log('Sending CSV process request with data:', processData);
              const response = await apiService.processCSV(processData);
              console.log('CSV process response:', response.data);
              
              if (response.status === 'success') {
                const importData = response.data;
                
                // Convert imported data to ExtractedReport format
                const report: ExtractedReport = {
                  filename: uploadFile.file.name,
                  testDate: null, // CSV imports may have multiple dates
                  suggestedDate: undefined,
                  dateConfidence: 1.0,
                  results: [], // Will be populated from saved reports
                  warnings: importData.warnings || [],
                  rawText: `Imported ${importData.imported_results} results from ${importData.imported_reports} reports`
                };
                
                // Mark file as extracted
                setSession(prev => ({
                  ...prev,
                  files: prev.files.map(f => 
                    f.id === uploadFile.id 
                      ? { ...f, status: 'extracted', extractedData: report } 
                      : f
                  )
                }));
                
                // Show success message
                setToastMessage(`AI successfully imported ${importData.imported_results} lab results from ${importData.imported_reports} date(s)`);
                setShowSuccessToast(true);
              } else {
                throw new Error(response.message || response.error || 'Import failed');
              }
            } catch (error: any) {
              console.error('CSV import error:', error);
              console.error('Error response:', error.response?.data);
              const errorMessage = error.response?.data?.message || error.message || 'Import failed';
              setSession(prev => ({
                ...prev,
                files: prev.files.map(f => 
                  f.id === uploadFile.id 
                    ? { ...f, status: 'error', error: errorMessage } 
                    : f
                )
              }));
            }
          } else {
            // This shouldn't happen with LLM parser
            console.error('Unexpected response from CSV preview');
            setSession(prev => ({
              ...prev,
              files: prev.files.map(f => 
                f.id === uploadFile.id 
                  ? { ...f, status: 'error', error: 'Unexpected file format' } 
                  : f
              )
            }));
          }
          
          // Return early - actual processing will happen after column mapping or auto-processing
          return;
        }
        
        // Regular PDF processing continues here
        console.log('=== PDF EXTRACTION DEBUG ===');
        console.log('File:', uploadFile.file.name);
        console.log('Extracted data keys:', Object.keys(extractedData));
        console.log('File hash:', extractedData.fileHash);
        console.log('extractedValues:', extractedData.extractedValues);
        console.log('extractedValues type:', typeof extractedData.extractedValues);
        console.log('extractedValues is null?', extractedData.extractedValues === null);
        console.log('extractedValues is undefined?', extractedData.extractedValues === undefined);
        console.log('extractedValues entries:', Object.entries(extractedData.extractedValues || {}).length);
        
        // Convert extractedValues object to array format
        console.log('Converting extractedValues to results array...');
        const results = Object.entries(extractedData.extractedValues || {}).map(([testName, data]: [string, any]) => {
          // Parse reference range
          let referenceMin = null;
          let referenceMax = null;
          let status: 'normal' | 'high' | 'low' | 'unknown' = 'unknown';
          
          if (data.reference_range) {
            const rangeMatch = data.reference_range.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
            if (rangeMatch) {
              referenceMin = parseFloat(rangeMatch[1]);
              referenceMax = parseFloat(rangeMatch[2]);
              
              if (data.value < referenceMin) {
                status = 'low';
              } else if (data.value > referenceMax) {
                status = 'high';
              } else {
                status = 'normal';
              }
            }
          }
          
          // Use qualitative result if available
          if (data.qualitative_result) {
            status = data.qualitative_result.toLowerCase() as any;
          }
          
          return {
            testName,
            value: data.value,
            units: data.units,
            referenceMin,
            referenceMax,
            status,
            confidence: data.confidence || 0.95,
            originalText: data.raw_text
          };
        });
        
        console.log('Results array created:', results.length, 'items');
        if (results.length > 0) {
          console.log('First result:', results[0]);
        }
        
        // Stage 5: Analyze results
        setProcessingStage(4);
        setProcessingProgress(baseProgress + (stageProgress * 0.7));
        
        // Apply auto-corrections for units with >50% confidence
        const correctedResults = await applyCorrectionsSilently(results, extractedData.testDate);
        console.log('Corrected results:', correctedResults.length, 'items');
        
        // Stage 6: Validate accuracy
        setProcessingStage(5);
        setProcessingProgress(baseProgress + (stageProgress * 0.85));
        
        // Convert to our ExtractedReport format
        // Priority: extracted date > user-provided date
        const finalTestDate = extractedData.testDate || userProvidedTestDate;
        const report: ExtractedReport = {
          filename: uploadFile.file.name,
          fileHash: extractedData.fileHash,
          testDate: finalTestDate,
          suggestedDate: extractedData.testDate || userProvidedTestDate,
          dateConfidence: extractedData.testDate ? 1.0 : 0.8, // Higher confidence if user provided
          results: correctedResults,
          warnings: [],
          rawText: extractedData.rawText
        };
        
        // Check for duplicates
        let isDuplicate = false;
        let duplicateInfo: any = null;
        let duplicateCheckSkipped = false;
        
        // First check within current batch
        if (extractedData.fileHash) {
          // console.log(`File: ${uploadFile.file.name}, Hash: ${extractedData.fileHash}`);
          // console.log('Current batch hashes:', Array.from(batchFileHashesRef.current.entries()));
          
          const existingFileInfo = batchFileHashesRef.current.get(extractedData.fileHash);
          if (existingFileInfo && existingFileInfo.id !== uploadFile.id) {
            isDuplicate = true;
            duplicateInfo = {
              status: 'duplicate',
              duplicateType: 'batch',
              message: `Duplicate of "${existingFileInfo.filename}" in this batch`,
              existingReport: { filename: existingFileInfo.filename }
            };
            
            report.warnings.push({
              type: 'duplicate' as any,
              field: 'file',
              message: `This file is a duplicate of "${existingFileInfo.filename}" in the current batch`,
              severity: 'error'
            });
            
            console.log(`DUPLICATE DETECTED: ${uploadFile.file.name} is duplicate of ${existingFileInfo.filename}`);
          } else {
            // Add to batch hash map
            batchFileHashesRef.current.set(extractedData.fileHash, { 
              id: uploadFile.id, 
              filename: uploadFile.file.name 
            });
            // console.log(`Added to batch: ${extractedData.fileHash} -> ${uploadFile.id}`);
          }
        } else {
          console.warn('No fileHash in extracted data for:', uploadFile.file.name);
        }
        
        // Then check against database if not already marked as duplicate
        if (!isDuplicate && extractedData.fileHash) {
          try {
            // Only proceed with duplicate check if we have auth
            if (user && isAuthenticated) {
              // console.log(`Checking duplicate for ${uploadFile.file.name}`);
              
              const dupCheckResponse = await apiService.checkDuplicate({
                filename: uploadFile.file.name,
                fileHash: extractedData.fileHash,
                testDate: extractedData.testDate
              });
              
              if (dupCheckResponse.status === 'error' && dupCheckResponse.error === 'duplicate') {
                isDuplicate = true;
                duplicateInfo = dupCheckResponse.data;
                
                report.warnings.push({
                  type: 'duplicate' as any,
                  field: 'file',
                  message: dupCheckResponse.message || 'Duplicate file detected',
                  severity: 'error'
                });
              }
            }
          } catch (error: any) {
            console.error('Error checking for duplicates:', error);
            
            // If it's still a 401 after retries, skip future checks
            if (error.response?.status === 401) {
              console.error('Persistent 401 error. Skipping backend calls for this session.');
              // Mark to skip duplicate checks for remaining files
              duplicateCheckSkipped = true;
              setSkipBackendCalls(true);
              skipBackendCallsRef.current = true;
            }
          }
        }
        
        // Mark the file if it's a duplicate
        if (isDuplicate) {
          setSession(prev => ({
            ...prev,
            files: prev.files.map(f => 
              f.id === uploadFile.id 
                ? { ...f, isDuplicate: true, duplicateInfo } 
                : f
            )
          }));
        }

        // Add warnings
        if (!report.testDate) {
          report.warnings.push({
            type: 'missing_date',
            field: 'testDate',
            message: 'Test date could not be extracted. Please enter it manually.',
            severity: 'error'
          });
        }

        // Check for low confidence values
        report.results.forEach(result => {
          if (result.confidence < 0.7) {
            report.warnings.push({
              type: 'low_confidence',
              field: result.testName,
              message: `Low confidence for ${result.testName}. Please verify the value.`,
              severity: 'warning'
            });
          }
        });

        // Update files processed count
        setFilesProcessed(prev => prev + 1);
        
        // Show completion stage briefly for last file
        if (fileIndex === totalFiles - 1) {
          setProcessingStage(6);
          setProcessingProgress(100);
        }
        
        setSession(prev => ({
          ...prev,
          files: prev.files.map(f => 
            f.id === uploadFile.id 
              ? { 
                  ...f, 
                  status: 'extracted', 
                  extractedData: report,
                  duplicateCheckSkipped // Mark if duplicate check was skipped
                } 
              : f
          )
        }));
        
        // Don't hide overlay here - let processFilesSequentially handle it
      }
    } catch (error) {
      console.error('Extraction error:', error);
      setSession(prev => ({
        ...prev,
        files: prev.files.map(f => 
          f.id === uploadFile.id 
            ? { ...f, status: 'error', error: 'Failed to extract data' } 
            : f
        ),
        hasErrors: true
      }));
      // Don't hide overlay here - let processFilesSequentially handle it
    }
  }, [applyCorrectionsSilently]);

  // Process files one by one to catch duplicates within the batch
  const processFilesSequentially = useCallback(async (files: UploadFile[]) => {
    // Show overlay and reset counters
    setIsShowingProcessingOverlay(true);
    setTotalFilesToProcess(files.length);
    setFilesProcessed(0);
    setProcessingStage(0);
    setProcessingProgress(0);
    
    for (let i = 0; i < files.length; i++) {
      await extractDataFromFile(files[i], i, files.length);
    }
    
    // Hide overlay after all files are processed
    setTimeout(() => {
      setIsShowingProcessingOverlay(false);
    }, 1500);
  }, [extractDataFromFile, skipBackendCalls]);

  // Add new files to the session
  const handleFilesAdded = useCallback((newFiles: File[]) => {
    const uploadFiles: UploadFile[] = newFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      file,
      status: 'pending'
    }));

    setSession(prev => ({
      ...prev,
      files: [...prev.files, ...uploadFiles]
    }));

    // Process files sequentially to catch duplicates within batch
    processFilesSequentially(uploadFiles);
  }, [processFilesSequentially]);

  // Check if all files are processed and show the results (don't auto-save)
  useEffect(() => {
    const allProcessed = session.files.length > 0 && 
      session.files.every(f => f.status === 'extracted' || f.status === 'error');
    
    if (allProcessed && !hasCompletedSave) {
      setSession(prev => ({ ...prev, allExtracted: true }));
      // Show the complete step with DataTable for review
      setCurrentStep('complete');
      setHasCompletedSave(true); // Prevent re-triggering
    }
  }, [session.files, hasCompletedSave]);

  // Remove a file from the session (only used during upload phase)
  const handleRemoveFile = (fileId: string) => {
    setSession(prev => ({
      ...prev,
      files: prev.files.filter(f => f.id !== fileId)
    }));
    
    // Also remove from batch hashes if it exists
    const fileToRemove = session.files.find(f => f.id === fileId);
    if (fileToRemove?.extractedData?.rawText) {
      // Find and remove the hash entry
      batchFileHashesRef.current.forEach((value, key) => {
        if (value.id === fileId) {
          batchFileHashesRef.current.delete(key);
        }
      });
    }
  };

  const extractedFiles = session.files.filter(f => f.status === 'extracted');
  const processingFiles = session.files.filter(f => f.status === 'processing');
  const errorFiles = session.files.filter(f => f.status === 'error');
  const duplicateFiles = session.files.filter(f => f.isDuplicate);

  // Handle value correction
  const handleValueCorrection = (marker: string, correctedValue: number, correctedUnit?: string) => {
    const currentFile = extractedFiles[selectedFileIndex];
    if (!currentFile) return;
    
    // Create a unique key for the correction that includes the file ID
    const correctionKey = `${currentFile.id}:${marker}`;
    const correctionData = { 
      value: correctedValue, 
      unit: correctedUnit,
      fileId: currentFile.id 
    };
    
    console.log(`Storing correction for ${marker}:`, correctionData);
    
    setCorrections(prev => new Map(prev).set(correctionKey, correctionData));
    
    // Update the file's extractedData with the correction
    if (currentFile?.extractedData) {
      const updatedResults = currentFile.extractedData.results.map(result => {
        if (result.testName === marker) {
          return {
            ...result,
            isEdited: true
          };
        }
        return result;
      });
      
      // Update the session state
      setSession(prev => ({
        ...prev,
        files: prev.files.map(f => 
          f.id === currentFile.id 
            ? { 
                ...f, 
                extractedData: {
                  ...f.extractedData!,
                  results: updatedResults
                }
              } 
            : f
        )
      }));
    }
    
    // Show success toast with specific message
    setToastMessage(`${marker} value corrected successfully`);
    setShowSuccessToast(true);
  };
  
  // Convert ExtractedLabResult to LabValue format for DataTable
  const convertToLabValues = (results: ExtractedLabResult[]): Map<string, LabValue> => {
    const labValues = new Map<string, LabValue>();
    const currentFile = extractedFiles[selectedFileIndex];
    
    results.forEach(result => {
      const correctionKey = currentFile ? `${currentFile.id}:${result.testName}` : result.testName;
      const correction = corrections.get(correctionKey);
      labValues.set(result.testName, {
        value: result.value,
        units: result.units || '',
        confidence: result.confidence * 100, // Convert to percentage
        qualitative_result: result.status,
        reference_range: result.referenceMin && result.referenceMax 
          ? `${result.referenceMin}-${result.referenceMax}` 
          : undefined,
        raw_text: result.originalText,
        // Add corrected values if they exist
        corrected_value: correction?.value,
        corrected_units: correction?.unit
      });
    });
    
    return labValues;
  };

  // Calculate remaining issues
  const getRemainingIssues = () => {
    if (!extractedFiles[selectedFileIndex]?.extractedData) return 0;
    
    const results = extractedFiles[selectedFileIndex].extractedData!.results;
    const labValues = convertToLabValues(results);
    const validationResults = ExtractionValidationService.validateLabResults(labValues);
    const problematicMarkers = ExtractionValidationService.getProblematicMarkers(validationResults);
    
    // Count how many problematic markers have been corrected for this specific file
    const currentFile = extractedFiles[selectedFileIndex];
    const correctedCount = problematicMarkers.filter(marker => {
      const correctionKey = `${currentFile.id}:${marker}`;
      return corrections.has(correctionKey);
    }).length;
    
    return Math.max(0, problematicMarkers.length - correctedCount);
  };
  
  // Handle save with corrections applied
  const handleSaveWithCorrections = async () => {
    setCurrentStep('processing');
    setIsSaving(true);
    
    // Debug log corrections
    console.log('Corrections to apply:', Array.from(corrections.entries()));
    
    try {
      // Apply corrections to all extracted reports
      const reportsWithCorrections = extractedFiles
        .filter(f => f.extractedData && !f.isDuplicate)
        .map(file => {
          const report = { ...file.extractedData! };
          
          // Apply corrections to this report's results
          report.results = report.results.map(result => {
            const correctionKey = `${file.id}:${result.testName}`;
            const correction = corrections.get(correctionKey);
            if (correction && correction.unit && correction.unit !== result.units) {
              // Convert reference ranges if units changed
              let convertedRefMin = result.referenceMin;
              let convertedRefMax = result.referenceMax;
              
              if (result.referenceMin !== null && result.referenceMax !== null) {
                // Detect the likely unit of the reference range
                const detectedUnit = detectReferenceRangeUnit(
                  result.testName,
                  result.referenceMin,
                  result.referenceMax
                );
                
                const fromUnit = detectedUnit || result.units || 'mg/dL';
                const converted = convertReferenceRange(
                  result.testName,
                  result.referenceMin,
                  result.referenceMax,
                  fromUnit,
                  correction.unit
                );
                
                convertedRefMin = converted.min;
                convertedRefMax = converted.max;
                
                console.log(`Converting reference range for ${result.testName}:`, {
                  original: { min: result.referenceMin, max: result.referenceMax, unit: fromUnit },
                  converted: { min: convertedRefMin, max: convertedRefMax, unit: correction.unit }
                });
              }
              
              console.log(`Applying correction for ${result.testName}:`, {
                original: { value: result.value, units: result.units },
                corrected: { value: correction.value, units: correction.unit }
              });
              
              return {
                ...result,
                value: correction.value,
                units: correction.unit,
                referenceMin: convertedRefMin,
                referenceMax: convertedRefMax,
                isEdited: true
              };
            } else if (correction) {
              // Just value correction, no unit change
              return {
                ...result,
                value: correction.value,
                isEdited: true
              };
            }
            return result;
          });
          
          return report;
        });
      
      // Save the corrected reports
      if (reportsWithCorrections.length > 0) {
        console.log('Sending corrected reports to backend:', reportsWithCorrections);
        await onComplete(reportsWithCorrections);
      }
      
      // Navigate to trends after successful save
      setTimeout(() => {
        if (onNavigateToTrends) {
          onNavigateToTrends();
        } else {
          // Fallback if callback not provided
          window.location.href = '/trends';
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error saving reports with corrections:', error);
      setIsSaving(false);
      setSession(prev => ({ ...prev, hasErrors: true }));
      setCurrentStep('complete');
      
      // Show error message
      setToastMessage('Failed to save corrections. Please try again.');
      setShowSuccessToast(true);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Processing Overlay */}
      <ProcessingOverlay 
        isProcessing={isShowingProcessingOverlay}
        currentStage={processingStage}
        progress={processingProgress}
        filesProcessed={filesProcessed}
        totalFiles={totalFilesToProcess}
      />
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <Step 
            number={1} 
            title="Upload PDFs" 
            status={currentStep === 'upload' ? 'active' : 'complete'} 
          />
          <div className="flex-1 h-1 bg-gray-200 mx-4" />
          <Step 
            number={2} 
            title="Process & Save" 
            status={currentStep === 'processing' ? 'active' : currentStep === 'complete' ? 'complete' : 'pending'} 
          />
        </div>
      </div>

      {/* Content based on current step */}
      {currentStep === 'upload' && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Upload Lab Reports
          </h2>
          
          {/* Date picker for test date */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label htmlFor="testDate" className="block text-sm font-medium text-gray-700 mb-2">
              When were these lab tests performed?
            </label>
            <input 
              id="testDate"
              type="date" 
              value={userProvidedTestDate}
              onChange={(e) => setUserProvidedTestDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]} // Can't be future
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <p className="mt-2 text-sm text-gray-600">
              This date will be used for all PDFs in this batch. The system will still try to extract dates from each PDF.
            </p>
          </div>
          
          <FileUploader
            onFilesAdded={handleFilesAdded}
            uploadedFiles={session.files}
            maxFiles={25}
            onRemoveFile={handleRemoveFile}
          />
          
          {/* Show processing status */}
          {processingFiles.length > 0 && (
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                <p className="text-blue-800">Processing {processingFiles.length} file(s)...</p>
              </div>
            </div>
          )}
          
          {/* Show completion status */}
          {session.allExtracted && session.files.length > 0 && (
            <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">
                ✅ All files processed successfully! 
                {duplicateFiles.length > 0 && ` (${duplicateFiles.length} duplicate(s) detected)`}
              </p>
            </div>
          )}
        </div>
      )}

      {currentStep === 'processing' && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Processing Reports
          </h2>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mr-4"></div>
              <p className="text-lg text-gray-700">
                {isSaving ? 'Saving your lab reports...' : 'Processing complete, finalizing...'}
              </p>
            </div>
            {isSaving && (
              <p className="text-sm text-gray-500 text-center mt-4">
                This may take a moment, please don't close this window.
              </p>
            )}
          </div>
        </div>
      )}

      {currentStep === 'complete' && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Extracted Lab Results
          </h2>
          
          {/* File selector if multiple files */}
          {extractedFiles.length > 1 && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mr-2">
                Select report to view:
              </label>
              <select 
                value={selectedFileIndex}
                onChange={(e) => setSelectedFileIndex(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                {extractedFiles.map((file, index) => (
                  <option key={file.id} value={index}>
                    {file.file.name} {file.extractedData?.testDate ? `(${file.extractedData.testDate})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Error message if save failed */}
          {session.hasErrors && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">
                ⚠️ There was an issue saving the reports to the database, but your extracted data is shown below.
              </p>
            </div>
          )}
          
          {/* DataTable with extracted results */}
          {extractedFiles.length > 0 && extractedFiles[selectedFileIndex]?.extractedData && (
            <div className="mb-6">
              <DataTable 
                results={convertToLabValues(extractedFiles[selectedFileIndex].extractedData!.results)}
                onMarkerClick={handleMarkerClick}
                selectedMarker={selectedMarker}
                onValueCorrected={handleValueCorrection}
              />
            </div>
          )}
          
          {/* LLM Explanation Modal */}
          {selectedMarker && (
            <LLMExplanation
              explanation={llmExplanation}
              isLoading={isLoadingExplanation}
              selectedMarker={selectedMarker}
              error={explanationError}
              onClose={() => {
                setSelectedMarker(null);
                setLlmExplanation(null);
                setExplanationError(null);
              }}
            />
          )}
          
          {/* Summary and Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold mb-2">Processing Summary</h3>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-600">
                    Files processed: <span className="font-medium">{session.files.length}</span>
                  </p>
                  <p className="text-gray-600">
                    Successfully saved: <span className="font-medium text-green-600">{extractedFiles.filter(f => !f.isDuplicate).length}</span>
                  </p>
                  {duplicateFiles.length > 0 && (
                    <p className="text-gray-600">
                      Duplicates skipped: <span className="font-medium text-orange-600">{duplicateFiles.length}</span>
                    </p>
                  )}
                  {errorFiles.length > 0 && (
                    <p className="text-gray-600">
                      Errors: <span className="font-medium text-red-600">{errorFiles.length}</span>
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col gap-3">
                {getRemainingIssues() > 0 ? (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                      <p className="text-sm text-amber-800 font-medium">
                        ⚠️ {getRemainingIssues()} issue{getRemainingIssues() !== 1 ? 's' : ''} remaining
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        Review and correct these before proceeding
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setHasReviewedData(true);
                        handleSaveWithCorrections();
                      }}
                      className="px-6 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium"
                    >
                      📊 View Trends Anyway →
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setHasReviewedData(true);
                      // Apply corrections and save before navigating
                      handleSaveWithCorrections();
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
                  >
                    📊 View Trends →
                  </button>
                )}
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
                >
                  📤 Upload More Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      
      {/* Success Toast */}
      <SuccessToast 
        show={showSuccessToast}
        message={toastMessage}
        onClose={() => setShowSuccessToast(false)}
      />
      
    </div>
  );
};

// Step indicator component
const Step: React.FC<{ number: number; title: string; status: 'pending' | 'active' | 'complete' }> = ({
  number,
  title,
  status
}) => {
  const getColors = () => {
    switch (status) {
      case 'complete':
        return 'bg-green-600 text-white';
      case 'active':
        return 'bg-blue-600 text-white';
      default:
        return 'bg-gray-300 text-gray-600';
    }
  };

  return (
    <div className="flex items-center">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getColors()}`}>
        {status === 'complete' ? '✓' : number}
      </div>
      <span className={`ml-2 text-sm font-medium ${status === 'pending' ? 'text-gray-500' : 'text-gray-900'}`}>
        {title}
      </span>
    </div>
  );
};